import React, { FunctionComponent, useEffect, useMemo, useState } from "react"
import { unstable_batchedUpdates } from "react-dom"
import { FormProvider, useForm } from "react-hook-form"
import { store } from "react-notifications-component"
import { Route, useHistory, useRouteMatch } from "react-router"
import { notificationOptions } from "../../../api/Definitions"
import Loader from "../../../components/Loader/Loader"
import Seo from "../../../components/Seo/Seo"
import { iAccess } from "../../../definitions/Access"
import { iArTarget } from "../../../definitions/ArTarget"
import {
  iInspectorProps,
  iResponse,
  iUrlParams,
} from "../../../definitions/interfaces"
import { ItemWithAccess } from "../../../definitions/types"
import { errorNotification } from "../../../helpers/notificationFunctions"
import {
  get,
  handleDeleteData,
  handleUpdateData,
} from "../../../store/data/actions"
import ARTargetHeader from "../Header"
import ARTargetOverview from "./Overview"
import ARTargetUploads from "./Uploads"

const ArTargetInspector: FunctionComponent<iInspectorProps<iArTarget>> = ({
  item,
  access,
  reload,
}) => {
  const [isLoading, setLoading] = useState(false)
  const match = useRouteMatch<iUrlParams>()
  const history = useHistory()

  const methods = useForm<ItemWithAccess<iArTarget>>({
    shouldUnregister: false,
    defaultValues: { ...item, __access: access },
  })

  const {
    formState: { dirtyFields },
  } = methods

  useEffect(() => {
    methods.reset({ ...item, __access: access })
  }, [item, access])

  const onSubmit = (data: ItemWithAccess<iArTarget>) => {
    setLoading(true)
    handleUpdateData({
      data,
      fields: dirtyFields as Partial<ItemWithAccess<iArTarget>>,
      link: "/v1/ar-targets",
    })
      .then(() => {
        setLoading(false)
        reload()
        store.addNotification({
          title: "Success!",
          message: "AR target successfully updated!",
          type: "success",
          ...notificationOptions,
        })
      })
      .catch((e) => {
        errorNotification(e)
        setLoading(false)
      })
  }

  const onDelete = () => {
    setLoading(true)
    if (confirm("Are you sure you want to delete this?")) {
      handleDeleteData({
        link: "/v1/ar-targets/" + item._id,
      })
        .then(() => {
          setLoading(false)
          store.addNotification({
            title: "Success!",
            message: "AR target successfully deleted!",
            // message: "teodosii@react-notifications-component",
            type: "success",
            ...notificationOptions,
          })
          history.goBack()
        })
        .catch((e) => {
          if (e) {
            if (e.reason === "Nothing changed!") {
              store.addNotification({
                message: e.reason,
                type: "warning",
                ...notificationOptions,
              })
            } else {
              store.addNotification({
                message: e.reason,
                type: "danger",
                ...notificationOptions,
              })
            }
          } else {
            store.addNotification({
              message: "Something went wrong!",
              type: "danger",
              ...notificationOptions,
            })
          }
          setLoading(false)
        })
    }
  }

  const invokeSubmit = () => {
    methods.handleSubmit((data) => {
      onSubmit(data as ItemWithAccess<iArTarget>)
    })()
  }

  return (
    <FormProvider {...methods}>
      <Seo title={`${item.name ? item.name : "Inspector"} | VR-All-Art`} />
      <ARTargetHeader
        title={item.name || "Inspector"}
        submitText="Update"
        submitDeleteText={"Delete"}
        isLoading={isLoading}
        handleSubmit={invokeSubmit}
        handleDelete={onDelete}
      />
      {isLoading ? (
        <Loader />
      ) : (
        <div className="inspector">
          <form
            onSubmit={methods.handleSubmit((data) => {
              onSubmit(data as ItemWithAccess<iArTarget>)
            })}
            className="inspector-form"
          >
            <Route exact path={match.path}>
              <ARTargetOverview />
            </Route>
            <Route exact path={match.path + "/manage-files"}>
              <ARTargetUploads item={item} />
            </Route>
            <button className="d-none" type="submit">
              Submit
            </button>
          </form>
        </div>
      )}
    </FormProvider>
  )
}

const InspectorLoader: FunctionComponent = () => {
  const [isLoading, setLoading] = useState(true)
  const [access, setAccess] = useState<iAccess>()
  const [item, setItem] = useState<iArTarget>()
  const match = useRouteMatch<iUrlParams>()

  const load = () => {
    Promise.all([
      get<iResponse<iAccess>>({
        link: "/v1/access",
        query: { collection: "ARTargets", url: match.params.url },
      }),
      get<iResponse<iArTarget>>({ link: "/v1/ar-targets/" + match.params.url }),
    ]).then((result) => {
      const [accessResult, dataResult] = result
      unstable_batchedUpdates(() => {
        setItem(dataResult.data)
        setAccess(accessResult.data)
        setLoading(false)
      })
    })
  }

  useMemo(() => {
    load()
  }, [])

  return isLoading ? (
    <Loader />
  ) : item && access ? (
    <ArTargetInspector item={item} access={access} reload={load} />
  ) : null
}

export default InspectorLoader
